import { ORDERS_LISTING_PATH } from '@constants/cp-paths';
import { strings } from '@constants/strings';
import { getTenantFromURL } from '@utils/app-utils';

const tenantConfig = {
  'OLX-PK': {
    allow_house_landmark_edit: true,
    handle_self_delivery: true,
    iban_regex: /^PK[0-9]{2}[A-Z]{4}[0-9]{16}$/,
    allow_signup: true,
    order_statuses: {
      accepted_by_seller: {
        slug: 'awaiting-packaging',
      },
    },
    dashboard: {
      cp: {
        payments_summary: true,
        products: true,
        average_order_time: true,
      },
    },
    qrcode_generation_enabled: true,
    tax_verification_enabled: false,
    side_menu: {
      cp: {
        orders: {
          path: `${ORDERS_LISTING_PATH}?f[disposition.slug]=awaiting-buyer-confirmation`,
        },
        advance_payment: {
          show: true,
        },
        carts: {
          show: true,
        },
        calls: {
          show: true,
        },
        store_uploads: {
          show: true,
        },
        location_settings: {
          show: true,
        },
      },
      seller_center: {
        return_orders: false,
      },
    },
    constants: {
      consumer_text: strings?.go_to_olx,
      consumer_external_id_text: strings?.olx_external_id,
    },
    user: {
      cp: {
        update: {
          name: {
            show: true,
            required: true,
          },
          mobile: {
            show: true,
          },
          email: {
            show: true,
            disabled: true,
          },
          role: {
            show: true,
          },
        },
        create: true,
      },
      seller_center: {
        add: {
          password: {
            show: true,
          },
        },
      },
    },
    transaction_history: {
      upload_fulfillment: true,
    },
    seller: {
      listing: {
        actions: {
          send_welcome_email: true,
          publish_seller: true,
          show_resync_draft_products: true,
        },
        columns: {
          products_count: {
            show_published_count: true,
          },
        },
      },
      add_seller: {
        area: {
          show: true,
          required: true,
        },
        iban: {
          show: true,
          required: false,
        },
        logo: {
          show: true,
          required: false,
        },
        banners: {
          dimensions: { width: 1280, height: 160 },
          cropable: true,
        },
        mobile_banners: {
          dimensions: { width: 750, height: 308 },
          cropable: true,
        },
        ntn: {
          show: true,
          required: false,
          dependencies: ['govt_registered'],
        },
        cnic: {
          show: true,
          required: false,
        },
        bank: {
          show: true,
          required: false,
        },
        account_title: {
          show: true,
          required: false,
        },
        cnic_images: {
          show: true,
          required: false,
        },
        external_id: {
          show: true,
          required: false,
        },
        jarvis_external_id: {
          show: true,
          required: true,
        },
        preferred_pickup_time: {
          show: true,
          required: false,
        },
        account_manager: {
          show: true,
          required: false,
        },
        contract: {
          show: true,
          required: false,
        },
        seller_source: {
          show: true,
          required: false,
        },
        is_flagship: {
          show: true,
          required: true,
        },
        govt_registered: {
          show: true,
        },
        strn: {
          show: true,
        },
        three_pl_enabled: {
          show: true,
        },
      },
    },
    calls: {
      listings: {
        transcriptions: true,
      },
    },
    product: {
      filters: {
        seller_source: true,
      },
      add_product: {
        advance_payment: {
          show: true,
          required: false,
        },
        images: {
          show: true,
          required: 1,
        },
        description: {
          min: 20,
        },
        price: {
          min: 25,
          max: 1000000,
        },
        package_size: {
          required: true,
        },
        allow_image_in_description: true,
        allow_video_in_description: true,
        allow_link_in_description: true,
        allow_custom_specifications: true,
        allow_custom_variant_types: true,
        update_category_fields_structure: true,
      },
    },
    finance: {
      transaction_history: {
        display_realized_shipping_charges: true,
      },
    },
    account_settings: {
      profile_settings: {
        basic_info: {
          bank: {
            show: true,
            required: true,
          },
          bank_account_title: {
            show: true,
            required: true,
          },
          iban: {
            required: true,
          },
          ntn: {
            show: true,
            required: true,
          },
          strn: {
            show: true,
          },
        },
        uploads_tab: {
          mobile_banner_dimension: {
            width: 750,
            height: 308,
          },
          desktop_banner_dimension: {
            width: 1280,
            height: 160,
          },
          logo_dimension: {
            width: 270,
            height: 90,
          },
          show: true,
        },
      },
      category_commission: {
        show_minimum_slab: true,
      },
    },
    cp_order: {
      listing: {
        seggregate_dispositions: true,
        actions: [strings?.edit_products, 'advance_payment', 'refund_payment_proof'],
        columns: {
          paid_amount: true,
          mobile_key: 'mobile_for_cp',
        },
      },
      paths: {
        pending: `${ORDERS_LISTING_PATH}?f[disposition.slug]=awaiting-buyer-confirmation`,
      },
    },
    app_download: true,
    moderation: true,
  },
  'DUBIZZLE-UAE': {
    qrcode_generation_enabled: false,
    iban_regex: /^[A-Z]{2}\d{2}[A-Z0-9]{4,30}$/,
    order_statuses: {
      accepted_by_seller: {
        slug: 'accepted-by-seller',
      },
    },
    dashboard: {
      cp: {
        products: true,
        payments_summary: true,
      },
    },
    side_menu: {
      cp: {
        return_orders: true,
        orders: {
          path: ORDERS_LISTING_PATH,
        },
      },
      seller_center: {
        return_orders: true,
        chat: true,
      },
    },
    constants: {
      consumer_text: strings?.go_to_dubizzle,
      consumer_external_id_text: strings?.dubizzle_external_id,
    },
    api: {
      cp: {
        warehouses: {
          exclude_store_on_detail: true,
        },
      },
    },
    user: {
      cp: {
        update: {
          role: {
            show: true,
          },
          role_only: true,
        },
      },
      seller_center: {
        add: {
          password: {
            show: true,
          },
        },
      },
    },
    account_settings: {
      profile_settings: {
        basic_info: {
          iban: {
            required: true,
          },
        },
      },
    },
    seller: {
      add_seller: {
        allow_seller_commission: true,
        allow_sellerwise_amount: true,
        fetch_cities: true,
        fetch_warehouses: true,
        show_delivery_charges: true,
        show_delivery_settings_section: true,
        amount_for_categories: {
          show: true,
          required: true,
        },
        iban: {
          show: true,
          required: true,
        },
        logo: {
          show: true,
          required: true,
        },
        banners: {
          dimensions: { width: 1280, height: 160 },
          required: true,
          cropable: true,
        },
        mobile_banners: {
          dimensions: { width: 750, height: 308 },
          cropable: true,
        },
        description: {
          show: true,
          required: true,
        },
        tax_number: {
          show: true,
          required: false,
        },
        trade_license: {
          show: true,
          required: true,
        },
        bank: {
          show: true,
          required: true,
        },
        account_title: {
          show: true,
          required: true,
        },
        external_id: {
          show: true,
          required: true,
        },
        package_size: {
          required: true,
        },
        max_delivery_days: {
          show: true,
        },
      },
    },
    product: {
      add_product: {
        category: {
          disabled_on_edit: true,
        },
        overview: {
          show: true,
          required: true,
        },
        asin: {
          show: true,
          required: false,
        },
        gtin: {
          show: true,
          required: false,
        },
        delivery_charges: {
          show: true,
          required: true,
        },
        expected_delivery_days: {
          show: true,
        },
        images: {
          show: true,
          required: 2,
        },
        description: {
          min: 0,
        },
        price: {
          min: 1,
        },
        variants_discount_percentage: {
          show: true,
        },
        allow_image_in_description: true,
        image_lazy_loading: true,
        fetch_cities: true,
        fetch_option_types: true,
        disable_brand_on_edit: true,
        show_delivery_charges: true,
        update_category_fields_structure: true,
      },
    },
    finance: {
      transaction_history: {
        display_realized_shipping_charges: false,
        columns: {
          return_amount: true,
        },
      },
    },
    cp_order: {
      listing: {
        columns: {
          mobile_key: 'mobile',
        },
      },
      paths: {
        pending: `${ORDERS_LISTING_PATH}?f[status.slug]=pending`,
      },
    },
  },
};

export default tenantConfig[getTenantFromURL()];
